import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Spinner,
  Progress,
  Tooltip,
  Button,
} from '@nextui-org/react';
import { FaSort, FaSortUp, FaSortDown, FaInfoCircle } from 'react-icons/fa';
import Icon from '../base/Icon';
import { apiKeyService, PerformanceData } from '../../services/apiKeyService';
import { DateRange } from './DateRangeSelector';

interface PerformanceTableProps {
  className?: string;
  dateRange?: DateRange;
  apiKeyId?: string;
}

type SortField =
  | 'endpoint'
  | 'total_requests'
  | 'avg_response_time'
  | 'p95_response_time'
  | 'error_rate';
type SortDirection = 'asc' | 'desc';

const PerformanceTable: React.FC<PerformanceTableProps> = ({
  className = '',
  dateRange,
  apiKeyId,
}) => {
  const [performanceData, setPerformanceData] = useState<PerformanceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('total_requests');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const loadPerformanceData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use date range if provided, otherwise use default
      const response = dateRange?.isAllTime
        ? await apiKeyService.getPerformanceMetrics(
            30,
            undefined,
            undefined,
            true,
            apiKeyId
          ) // All time with proper flag
        : dateRange?.startDate && dateRange?.endDate && !dateRange.isAllTime
          ? await apiKeyService.getPerformanceMetrics(
              30,
              dateRange.startDate,
              dateRange.endDate,
              false,
              apiKeyId
            )
          : await apiKeyService.getPerformanceMetrics(
              30,
              undefined,
              undefined,
              false,
              apiKeyId
            );
      // Ensure all numeric values are properly processed
      const processedData = (response.performance || []).map((item) => ({
        ...item,
        total_requests: Number(item.total_requests) || 0,
        avg_response_time: Number(item.avg_response_time) || 0,
        p95_response_time: Number(item.p95_response_time) || 0,
        avg_input_tokens: Number(item.avg_input_tokens) || 0,
        avg_output_tokens: Number(item.avg_output_tokens) || 0,
        error_rate: Number(item.error_rate) || 0,
      }));
      setPerformanceData(processedData);
    } catch (err) {
      console.error('Failed to load performance data:', err);
      setError('Failed to load performance data');
      setPerformanceData([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange, apiKeyId]);

  useEffect(() => {
    loadPerformanceData();
  }, [loadPerformanceData]);

  // Format endpoint names for better display
  const formatEndpoint = (endpoint: string) => {
    return endpoint
      .replace('/api/v1/', '')
      .replace('analyze/', '')
      .split('/')
      .map((part) =>
        part.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
      )
      .join(' / ');
  };

  // Sort data
  const getSortedData = () => {
    return [...performanceData].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'endpoint') {
        aValue = formatEndpoint(aValue as string);
        bValue = formatEndpoint(bValue as string);
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      const numA = Number(aValue);
      const numB = Number(bValue);

      return sortDirection === 'asc' ? numA - numB : numB - numA;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <Icon icon={FaSort} className="w-3 h-3 text-gray-400" />;
    }
    return sortDirection === 'asc' ? (
      <Icon icon={FaSortUp} className="w-3 h-3 text-primary" />
    ) : (
      <Icon icon={FaSortDown} className="w-3 h-3 text-primary" />
    );
  };

  const getPerformanceStatus = (responseTime: number, errorRate: number) => {
    if (errorRate > 5) return { color: 'danger' as const, label: 'Poor' };
    if (responseTime > 10000)
      return { color: 'danger' as const, label: 'Slow' };
    if (responseTime > 5000 || errorRate > 1)
      return { color: 'warning' as const, label: 'Average' };
    return { color: 'success' as const, label: 'Excellent' };
  };

  const getMaxValues = () => {
    if (!performanceData.length)
      return { maxRequests: 1, maxResponseTime: 1, maxTokens: 1 };

    return {
      maxRequests: Math.max(...performanceData.map((d) => d.total_requests)),
      maxResponseTime: Math.max(
        ...performanceData.map((d) => d.avg_response_time)
      ),
      maxTokens: Math.max(
        ...performanceData.map((d) => d.avg_input_tokens + d.avg_output_tokens)
      ),
    };
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <h3 className="text-lg font-semibold">Endpoint Performance</h3>
        </CardHeader>
        <CardBody className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <h3 className="text-lg font-semibold">Endpoint Performance</h3>
        </CardHeader>
        <CardBody className="text-center text-red-500 py-8">
          <p>{error}</p>
          <Button
            color="primary"
            variant="flat"
            className="mt-4"
            onPress={loadPerformanceData}
          >
            Retry
          </Button>
        </CardBody>
      </Card>
    );
  }

  const sortedData = getSortedData();
  const maxValues = getMaxValues();

  if (!sortedData.length) {
    return (
      <Card className={className}>
        <CardHeader>
          <h3 className="text-lg font-semibold">Endpoint Performance</h3>
        </CardHeader>
        <CardBody>
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <div className="text-center">
              <p className="text-sm font-medium mb-2">
                No performance data available
              </p>
              <p className="text-xs text-gray-400">
                Performance metrics will appear once you start making API calls
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex justify-between items-center w-full">
          <h3 className="text-lg font-semibold">Endpoint Performance</h3>
          <Tooltip content="Performance metrics show response times, token usage, and error rates for each endpoint">
            <Button isIconOnly variant="light" size="sm">
              <Icon icon={FaInfoCircle} className="w-4 h-4 text-gray-400" />
            </Button>
          </Tooltip>
        </div>
      </CardHeader>
      <CardBody>
        <div className="overflow-x-auto">
          <Table aria-label="Endpoint performance table" className="min-w-full">
            <TableHeader>
              <TableColumn>
                <Button
                  variant="light"
                  className="h-auto p-0 min-w-0 font-medium"
                  onPress={() => handleSort('endpoint')}
                >
                  <span className="flex items-center gap-1">
                    Endpoint {getSortIcon('endpoint')}
                  </span>
                </Button>
              </TableColumn>
              <TableColumn>
                <Button
                  variant="light"
                  className="h-auto p-0 min-w-0 font-medium"
                  onPress={() => handleSort('total_requests')}
                >
                  <span className="flex items-center gap-1">
                    Requests {getSortIcon('total_requests')}
                  </span>
                </Button>
              </TableColumn>
              <TableColumn>
                <Button
                  variant="light"
                  className="h-auto p-0 min-w-0 font-medium"
                  onPress={() => handleSort('avg_response_time')}
                >
                  <span className="flex items-center gap-1">
                    Avg Time {getSortIcon('avg_response_time')}
                  </span>
                </Button>
              </TableColumn>
              <TableColumn>
                <Button
                  variant="light"
                  className="h-auto p-0 min-w-0 font-medium"
                  onPress={() => handleSort('p95_response_time')}
                >
                  <span className="flex items-center gap-1">
                    P95 Time {getSortIcon('p95_response_time')}
                  </span>
                </Button>
              </TableColumn>
              <TableColumn>Token Usage</TableColumn>
              <TableColumn>
                <Button
                  variant="light"
                  className="h-auto p-0 min-w-0 font-medium"
                  onPress={() => handleSort('error_rate')}
                >
                  <span className="flex items-center gap-1">
                    Error Rate {getSortIcon('error_rate')}
                  </span>
                </Button>
              </TableColumn>
              <TableColumn>Status</TableColumn>
            </TableHeader>
            <TableBody>
              {sortedData.map((item, index) => {
                const status = getPerformanceStatus(
                  item.avg_response_time || 0,
                  item.error_rate || 0
                );
                const totalTokens =
                  (item.avg_input_tokens || 0) + (item.avg_output_tokens || 0);

                return (
                  <TableRow key={`${item.endpoint}-${index}`}>
                    <TableCell>
                      <div className="max-w-xs">
                        <p
                          className="text-sm font-medium truncate"
                          title={item.endpoint}
                        >
                          {formatEndpoint(item.endpoint)}
                        </p>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {item.total_requests.toLocaleString()}
                        </p>
                        <Progress
                          value={
                            (item.total_requests / maxValues.maxRequests) * 100
                          }
                          className="max-w-20"
                          size="sm"
                          color="primary"
                        />
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {(item.avg_response_time || 0).toFixed(0)}ms
                        </p>
                        <Progress
                          value={
                            ((item.avg_response_time || 0) /
                              maxValues.maxResponseTime) *
                            100
                          }
                          className="max-w-20"
                          size="sm"
                          color={
                            item.avg_response_time > 5000
                              ? 'warning'
                              : 'success'
                          }
                        />
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {(item.p95_response_time || 0).toFixed(0)}ms
                        </p>
                        <p className="text-xs text-gray-500">95th percentile</p>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {totalTokens.toFixed(0)}
                        </p>
                        <div className="flex gap-1 text-xs text-gray-500">
                          <span>
                            {(item.avg_input_tokens || 0).toFixed(0)}in
                          </span>
                          <span>/</span>
                          <span>
                            {(item.avg_output_tokens || 0).toFixed(0)}out
                          </span>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {(item.error_rate || 0).toFixed(2)}%
                        </p>
                        <Progress
                          value={Math.min(item.error_rate || 0, 10) * 10}
                          className="max-w-20"
                          size="sm"
                          color={
                            (item.error_rate || 0) > 5
                              ? 'danger'
                              : (item.error_rate || 0) > 1
                                ? 'warning'
                                : 'success'
                          }
                        />
                      </div>
                    </TableCell>

                    <TableCell>
                      <Chip color={status.color} variant="flat" size="sm">
                        {status.label}
                      </Chip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Summary Statistics */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Total Endpoints</p>
            <p className="text-lg font-bold">{sortedData.length}</p>
          </div>

          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Avg Response Time</p>
            <p className="text-lg font-bold">
              {(
                sortedData.reduce(
                  (sum, item) => sum + item.avg_response_time,
                  0
                ) / sortedData.length
              ).toFixed(0)}
              ms
            </p>
          </div>

          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Total Requests</p>
            <p className="text-lg font-bold">
              {sortedData
                .reduce((sum, item) => sum + item.total_requests, 0)
                .toLocaleString()}
            </p>
          </div>

          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-1">Overall Error Rate</p>
            <p className="text-lg font-bold">
              {(
                sortedData.reduce((sum, item) => sum + item.error_rate, 0) /
                sortedData.length
              ).toFixed(2)}
              %
            </p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default PerformanceTable;
